<template>
  <app-custom-dialog
    v-model="show"
    :footer="false"
    title="conversion_rates"
    width="900px"
  >
    <div slot="activator" class="conversion-modal">
      <div v-if="!baseUnitId" class="conversion-banner conversion-banner--info mb-4">
        <v-icon small color="#438ef2">fal fa-info-circle</v-icon>
        <span>{{ $t("select_unit_first") }}</span>
      </div>

      <template v-else>
        <div class="conversion-base-hint mb-3">
          {{ $t("base_unit") }}: <strong>{{ baseUnitLabel }}</strong>
        </div>

        <!-- Restock -->
        <div class="conversion-section">
          <div class="conversion-section-title">{{ $t("restock") }}</div>
          <div class="conversion-row">
            <div class="conversion-side">
              <div class="conversion-side-label">{{ $t("from_unit") }}</div>
              <div class="conversion-side-body">
                <v-text-field
                  v-model.number="restock.from_quantity"
                  type="number"
                  min="0"
                  outlined
                  hide-details
                  disabled
                  class="conversion-qty"
                />
                <v-select
                  v-model="restock.from_unit_id"
                  :items="unitsExcluding(restock.to_unit_id)"
                  item-text="label"
                  item-value="id"
                  :placeholder="$t('unit')"
                  :no-data-text="$t('no_data')"
                  outlined
                  hide-details
                  :error="duplicateRowKeys.has('restock') || noPathRowKeys.has('restock')"
                  class="conversion-unit"
                />
              </div>
            </div>
            <v-icon class="conversion-arrow" small>fal fa-arrow-right</v-icon>
            <div class="conversion-side">
              <div class="conversion-side-label">{{ $t("to_unit") }}</div>
              <div class="conversion-side-body">
                <v-text-field
                  v-model.number="restock.to_quantity"
                  type="number"
                  min="0"
                  outlined
                  hide-details
                  class="conversion-qty"
                />
                <v-select
                  v-model="restock.to_unit_id"
                  :items="unitsExcluding(restock.from_unit_id)"
                  item-text="label"
                  item-value="id"
                  :placeholder="$t('unit')"
                  :no-data-text="$t('no_data')"
                  outlined
                  hide-details
                  :error="duplicateRowKeys.has('restock') || noPathRowKeys.has('restock')"
                  class="conversion-unit"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Usage -->
        <div
          v-for="(row, index) in usages"
          :key="index"
          class="conversion-section"
        >
          <div class="conversion-section-head">
            <div class="conversion-section-title">
              {{ $t("usage") }} {{ index + 1 }}
            </div>
            <v-btn icon x-small color="error" @click="removeUsage(index)">
              <v-icon x-small>fal fa-trash</v-icon>
            </v-btn>
          </div>
          <div class="conversion-row">
            <div class="conversion-side">
              <div class="conversion-side-label">{{ $t("from_unit") }}</div>
              <div class="conversion-side-body">
                <v-text-field
                  v-model.number="row.from_quantity"
                  type="number"
                  min="0"
                  outlined
                  hide-details
                  disabled
                  class="conversion-qty"
                />
                <v-select
                  v-model="row.from_unit_id"
                  :items="unitsExcluding(row.to_unit_id)"
                  item-text="label"
                  item-value="id"
                  :placeholder="$t('unit')"
                  :no-data-text="$t('no_data')"
                  outlined
                  hide-details
                  :error="duplicateRowKeys.has(`usage-${index}`) || noPathRowKeys.has(`usage-${index}`)"
                  class="conversion-unit"
                  @change="syncUsageFromUnits"
                />
              </div>
            </div>
            <v-icon class="conversion-arrow" small>fal fa-arrow-right</v-icon>
            <div class="conversion-side">
              <div class="conversion-side-label">{{ $t("to_unit") }}</div>
              <div class="conversion-side-body">
                <v-text-field
                  v-model.number="row.to_quantity"
                  type="number"
                  min="0"
                  outlined
                  hide-details
                  class="conversion-qty"
                />
                <v-select
                  v-model="row.to_unit_id"
                  :items="unitsExcluding(row.from_unit_id)"
                  item-text="label"
                  item-value="id"
                  :placeholder="$t('unit')"
                  :no-data-text="$t('no_data')"
                  outlined
                  hide-details
                  :error="
                    duplicateRowKeys.has(`usage-${index}`) ||
                    noPathRowKeys.has(`usage-${index}`) ||
                    duplicateToUnitKeys.has(`usage-${index}`)
                  "
                  class="conversion-unit"
                  @change="syncUsageFromUnits"
                />
              </div>
            </div>
          </div>
        </div>

        <v-btn
          block
          depressed
          color="#DDEAFD"
          class="text-none primary--text rounded-pill mt-2"
          @click="addUsage"
        >
          <v-icon left small>fal fa-plus</v-icon>
          {{ $t("add_conversion") }}
        </v-btn>

        <div v-if="errorMsg" class="conversion-banner conversion-banner--error mt-4">
          <v-icon small color="#d73d30">fal fa-exclamation-circle</v-icon>
          <span>{{ errorMsg }}</span>
        </div>
      </template>

      <div class="conversion-actions mt-5">
        <v-btn
          depressed
          color="#EEF1F5"
          class="text-none rounded-pill flex-grow-1"
          @click="close"
        >
          {{ $t("cancel") }}
        </v-btn>
        <v-btn
          depressed
          color="primary"
          class="text-none rounded-pill flex-grow-1"
          :disabled="!baseUnitId"
          @click="save"
        >
          {{ $t("save") }}
        </v-btn>
      </div>
    </div>
  </app-custom-dialog>
</template>

<script>
import {
  conversionFactor,
  hasConversionCycle,
} from "@/utils/ingredientConversion";

export default {
  name: "ConversionRatesModal",
  props: {
    value: { type: Boolean, default: false },
    units: { type: Array, default: () => [] },
    baseUnitId: { type: [Number, String], default: null },
    conversions: { type: Array, default: () => [] },
  },
  data() {
    return {
      restock: this.blankRestock(),
      usages: [],
      errorMsg: "",
    };
  },
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    baseUnit() {
      return (
        this.units.find((u) => Number(u.id) === Number(this.baseUnitId)) || null
      );
    },
    baseUnitLabel() {
      return this.baseUnit ? this.baseUnit.label : "-";
    },
    /** Row keys ("restock" / "usage-<index>") whose unit pair duplicates another row. */
    duplicateRowKeys() {
      const entries = [
        {
          key: "restock",
          from: this.restock.from_unit_id,
          to: this.restock.to_unit_id,
          started: this.rowStarted(this.restock),
        },
        ...this.usages.map((row, index) => ({
          key: `usage-${index}`,
          from: row.from_unit_id,
          to: row.to_unit_id,
          started: this.usageRowStarted(row),
        })),
      ].filter((e) => e.started && e.from && e.to);

      const pairMap = {};
      entries.forEach((e) => {
        const pair = [Number(e.from), Number(e.to)].sort((a, b) => a - b).join(":");
        pairMap[pair] = pairMap[pair] || [];
        pairMap[pair].push(e.key);
      });

      const duplicates = new Set();
      Object.values(pairMap).forEach((keys) => {
        if (keys.length > 1) keys.forEach((k) => duplicates.add(k));
      });
      return duplicates;
    },
    /** Usage row keys whose to_unit repeats another usage row's to_unit. */
    duplicateToUnitKeys() {
      const map = {};
      this.usages.forEach((row, index) => {
        if (!row.to_unit_id || !this.usageRowStarted(row)) return;
        const id = Number(row.to_unit_id);
        map[id] = map[id] || [];
        map[id].push(`usage-${index}`);
      });

      const duplicates = new Set();
      Object.values(map).forEach((keys) => {
        if (keys.length > 1) keys.forEach((k) => duplicates.add(k));
      });
      return duplicates;
    },
    /** Row keys ("restock" / "usage-<index>") that have no conversion path back to the base unit. */
    noPathRowKeys() {
      const entries = [
        { ...this.restock, purpose: "restock" },
        ...this.usages.map((row) => ({ ...row, purpose: "usage" })),
      ];

      const started = [
        { key: "restock", row: entries[0], ok: this.rowStarted(this.restock) },
        ...this.usages.map((row, index) => ({
          key: `usage-${index}`,
          row: entries[index + 1],
          ok: this.usageRowStarted(row),
        })),
      ].filter((e) => e.ok && e.row.from_unit_id && e.row.to_unit_id);

      const noPath = new Set();
      started.forEach((e) => {
        const purposes = e.row.purpose === "usage" ? ["usage"] : null;
        const fromOk =
          conversionFactor(entries, e.row.from_unit_id, this.baseUnitId, purposes) !==
          null;
        const toOk =
          e.row.purpose !== "usage" ||
          conversionFactor(entries, e.row.to_unit_id, this.baseUnitId, purposes) !==
            null;
        if (!fromOk || !toOk) noPath.add(e.key);
      });
      return noPath;
    },
  },
  watch: {
    value(open) {
      if (open) this.seed();
    },
    baseUnitId() {
      if (this.value) this.syncUsageFromUnits();
    },
  },
  methods: {
    /** Units list excluding the unit already chosen on the other side of the row. */
    unitsExcluding(excludeUnitId) {
      if (!excludeUnitId) return this.units;
      return this.units.filter((u) => Number(u.id) !== Number(excludeUnitId));
    },
    blankRestock() {
      return {
        id: null,
        from_unit_id: null,
        from_quantity: 1,
        to_unit_id: null,
        to_quantity: null,
      };
    },
    blankUsage() {
      return {
        id: null,
        from_unit_id: null,
        from_quantity: 1,
        to_unit_id: null,
        to_quantity: null,
      };
    },
    /** Usage 1 from defaults to base; each next row's from defaults to previous row's to.
     *  Only fills blank values — does not override a unit the user already picked. */
    syncUsageFromUnits() {
      this.usages.forEach((row, index) => {
        if (!row.from_unit_id) {
          row.from_unit_id =
            index === 0
              ? this.baseUnitId || null
              : this.usages[index - 1].to_unit_id || null;
        }
        if (
          row.to_unit_id &&
          row.from_unit_id &&
          Number(row.to_unit_id) === Number(row.from_unit_id)
        ) {
          row.to_unit_id = null;
        }
      });
    },
    // Deep-clone the incoming conversions into local editable state.
    seed() {
      this.errorMsg = "";
      const source = Array.isArray(this.conversions) ? this.conversions : [];

      const restock = source.find((c) => c.purpose === "restock");
      this.restock = restock
        ? {
            id: restock.id || null,
            from_unit_id: restock.from_unit_id || null,
            from_quantity: this.numberOrNull(restock.from_quantity) || 1,
            to_unit_id: restock.to_unit_id || null,
            to_quantity: this.numberOrNull(restock.to_quantity),
          }
        : this.blankRestock();

      this.usages = source
        .filter((c) => c.purpose === "usage")
        .map((c) => ({
          id: c.id || null,
          from_unit_id: c.from_unit_id || null,
          from_quantity: this.numberOrNull(c.from_quantity) || 1,
          to_unit_id: c.to_unit_id || null,
          to_quantity: this.numberOrNull(c.to_quantity),
        }));

      if (!this.usages.length) this.usages = [this.blankUsage()];
      this.syncUsageFromUnits();
    },
    addUsage() {
      this.usages.push(this.blankUsage());
      this.syncUsageFromUnits();
    },
    removeUsage(index) {
      this.usages.splice(index, 1);
      if (!this.usages.length) this.usages = [this.blankUsage()];
      this.syncUsageFromUnits();
    },
    close() {
      // Discard local edits; parent state is untouched.
      this.show = false;
    },
    save() {
      this.errorMsg = "";
      this.syncUsageFromUnits();
      const error = this.validate();
      if (error) {
        this.errorMsg = error;
        return;
      }
      this.$emit("save", this.buildRows());
      this.show = false;
    },
    validate() {
      const toUnitCounts = {};
      this.usages.forEach((row) => {
        if (!row.to_unit_id || !this.usageRowStarted(row)) return;
        const id = Number(row.to_unit_id);
        toUnitCounts[id] = (toUnitCounts[id] || 0) + 1;
      });
      if (Object.values(toUnitCounts).some((count) => count > 1)) {
        return this.$t("duplicate_to_unit");
      }

      const entries = [
        { ...this.restock, purpose: "restock" },
        ...this.usages.map((row) => ({ ...row, purpose: "usage" })),
      ].filter((row) =>
        row.purpose === "usage"
          ? this.usageRowStarted(row)
          : this.rowStarted(row)
      );

      const pairs = new Set();
      for (const row of entries) {
        if (!row.from_unit_id || !row.to_unit_id) {
          return this.$t("conversion_units_required");
        }
        if (
          Number(row.from_quantity) <= 0 ||
          Number(row.to_quantity) <= 0
        ) {
          return this.$t("validation_min_zero");
        }
        if (Number(row.from_unit_id) === Number(row.to_unit_id)) {
          return this.$t("conversion_units_must_be_different");
        }

        const unitIds = [Number(row.from_unit_id), Number(row.to_unit_id)].sort(
          (a, b) => a - b
        );
        const pair = unitIds.join(":");
        if (pairs.has(pair)) return this.$t("duplicate_conversion");
        pairs.add(pair);
      }

      if (hasConversionCycle(entries)) {
        return this.$t("conversion_cycle_not_allowed");
      }

      for (const row of entries) {
        if (!this.hasPathToBase(entries, row)) {
          return this.$t("conversion_path_to_base_required");
        }
      }

      return null;
    },
    hasPathToBase(entries, row) {
      const purposes = row.purpose === "usage" ? ["usage"] : null;
      const fromHasPath =
        conversionFactor(entries, row.from_unit_id, this.baseUnitId, purposes) !==
        null;

      if (row.purpose !== "usage") {
        return fromHasPath;
      }

      const toHasPath =
        conversionFactor(entries, row.to_unit_id, this.baseUnitId, purposes) !==
        null;
      return fromHasPath && toHasPath;
    },
    buildRows() {
      const rows = [];
      let sort = 0;

      if (this.rowStarted(this.restock)) {
        rows.push({
          ...(this.restock.id ? { id: this.restock.id } : {}),
          purpose: "restock",
          from_unit_id: this.restock.from_unit_id,
          from_quantity: Number(this.restock.from_quantity),
          to_unit_id: this.restock.to_unit_id,
          to_quantity: Number(this.restock.to_quantity),
          is_active: 1,
          sort_order: sort++,
        });
      }

      this.usages.forEach((row) => {
        if (!this.usageRowStarted(row)) return;
        rows.push({
          ...(row.id ? { id: row.id } : {}),
          purpose: "usage",
          from_unit_id: row.from_unit_id,
          from_quantity: Number(row.from_quantity),
          to_unit_id: row.to_unit_id,
          to_quantity: Number(row.to_quantity),
          is_active: 1,
          sort_order: sort++,
        });
      });

      return rows;
    },
    rowStarted(row) {
      return Boolean(
        row.from_unit_id || row.to_unit_id || Number(row.to_quantity) > 0
      );
    },
    usageRowStarted(row) {
      return Boolean(
        row.to_unit_id ||
          Number(row.to_quantity) > 0 ||
          (row.from_unit_id && Number(row.from_unit_id) !== Number(this.baseUnitId))
      );
    },
    numberOrNull(value) {
      if (value === null || value === undefined || value === "") return null;
      return Number(value);
    },
  },
};
</script>

<style scoped>
.conversion-modal {
  padding: 4px 2px;
}

.conversion-section {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 16px;
  margin-bottom: 16px;
  padding: 18px 20px;
}

.conversion-base-hint {
  color: #6b7280;
  font-size: 13px;
}

.conversion-section-head {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.conversion-section-title {
  color: #0f172a;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 12px;
}

.conversion-section-head .conversion-section-title {
  margin-bottom: 12px;
}

.conversion-row {
  align-items: flex-end;
  display: flex;
  gap: 14px;
}

.conversion-side {
  flex: 1;
  min-width: 0;
}

.conversion-side-label {
  color: #6b7280;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 8px;
}

.conversion-side-body {
  align-items: center;
  display: flex;
  gap: 10px;
}

.conversion-qty {
  max-width: 76px;
}

.conversion-unit {
  flex: 1;
  min-width: 0;
}

.conversion-base-unit {
  align-items: center;
  background: #eceff3;
  border-radius: 10px;
  color: #374151;
  display: flex;
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  height: 46px;
  min-width: 0;
  padding: 0 14px;
}

.conversion-arrow {
  color: #9ca3af;
  margin-bottom: 12px;
}

.conversion-actions {
  display: flex;
  gap: 12px;
  margin-top: 4px;
}

/* Soft, rounded banner replacing Vuetify's plain outlined v-alert —
   matches the tone used elsewhere in the import summary popups. */
.conversion-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.5;
}

.conversion-banner--info {
  background: #eef6ff;
  color: #1d4ed8;
}

.conversion-banner--error {
  background: #fdeceb;
  color: #b3261e;
}

/* Light gray filled inputs, taller and rounded — matching the modern
   ingredient-form / promotion-form field style instead of the old
   dense/bordered look. */
.conversion-modal ::v-deep .v-text-field--outlined .v-input__slot {
  background: #fff !important;
  border-radius: 10px !important;
  min-height: 46px;
}

.conversion-modal ::v-deep .v-text-field--outlined fieldset {
  border-color: #e2e8f0 !important;
  transition: border-color 0.2s ease;
}

.conversion-modal ::v-deep .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: #438ef2 !important;
  border-width: 1.5px !important;
}

.conversion-modal ::v-deep .v-input__append-inner {
  align-self: center !important;
  margin-top: 0 !important;
}

.conversion-modal ::v-deep .v-text-field__details {
  padding-left: 12px !important;
  margin-top: 4px !important;
}

/* Rounded-pill action buttons, matching the ingredient-form footer buttons */
.conversion-actions ::v-deep .v-btn,
.conversion-modal > .v-btn {
  border-radius: 999px !important;
}

.conversion-actions .v-btn {
  height: 46px !important;
  font-weight: 700;
}
</style>
